/**
 * Main Directory Page Component
 *
 * Provides React implementation of the employee directory with full feature parity
 * to the existing AngularJS version. Clean table-based interface matching AngularJS design.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Table, FilterControls } from '../../components/shared/ui';
import type { TableColumn, FilterOption } from '../../components/shared/ui';
import { useDirectoryApi } from './hooks/useDirectoryApi';
import type { Employee } from './hooks/useDirectoryApi';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setSelectedFacility } from '../../store/facilitiesSlice';
import '../../styles/common/index.css';
import './DirectoryPage.css';

interface DirectoryPageProps {
  /** Feature flag to control React vs AngularJS routing */
  useReactVersion?: boolean;
  /** Tenant configuration for customization */
  tenantConfig?: {
    singleUser?: boolean;
    environment?: string;
  };
}

// Mock data for testing and fallback
const MOCK_EMPLOYEES: Employee[] = [
  {
    id: '1',
    name: 'Dr. Sarah Johnson',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.johnson@tecinc.com',
    role: 'Physician',
    department: 'Emergency Medicine',
    facility: 'Main Hospital',
    status: 'active',
    phone: '(555) 123-4567',
    locations: ['Main Hospital', 'East Clinic']
  },
  {
    id: '2',
    name: 'Michael Chen',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@tecinc.com',
    role: 'Nurse Practitioner',
    department: 'Primary Care',
    facility: 'West Clinic',
    status: 'active',
    phone: '(555) 234-5678',
    locations: ['West Clinic']
  },
  {
    id: '3',
    name: 'Dr. Emily Rodriguez',
    firstName: 'Emily',
    lastName: 'Rodriguez',
    email: 'emily.rodriguez@tecinc.com',
    role: 'Cardiologist',
    department: 'Cardiology',
    facility: 'Main Hospital',
    status: 'active',
    phone: '(555) 345-6789',
    locations: ['Main Hospital', 'Cardiac Center']
  },
  {
    id: '4',
    name: 'David Wilson',
    firstName: 'David',
    lastName: 'Wilson',
    email: 'david.wilson@tecinc.com',
    role: 'Physical Therapist',
    department: 'Rehabilitation',
    facility: 'Rehab Center',
    status: 'active',
    phone: '(555) 456-7890',
    locations: ['Rehab Center']
  },
  {
    id: '5',
    name: 'Lisa Anderson',
    firstName: 'Lisa',
    lastName: 'Anderson',
    email: 'lisa.anderson@tecinc.com',
    role: 'Registered Nurse',
    department: 'Pediatrics',
    facility: 'Children\'s Wing',
    status: 'active',
    phone: '(555) 567-8901',
    locations: ['Children\'s Wing', 'NICU']
  }
];

const DirectoryPage: React.FC<DirectoryPageProps> = ({
  useReactVersion = true,
  tenantConfig: _tenantConfig
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux state for facilities (shared across app)
  const { facilities, selectedFacilityId } = useAppSelector(state => state.facilities);

  // Local state for directory-specific data
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [nameFilter, setNameFilter] = useState('');

  // Use directory API hook
  const { fetchEmployees, isLoading, error, clearError, getFilterOptions, transformEmployeeData } = useDirectoryApi();

  // Component lifecycle - load employees only (facilities loaded globally)
  useEffect(() => {
    loadEmployees();
  }, []);

  // No polling needed - facility changes are handled globally in Redux

  useEffect(() => {
    applyFilters();
  }, [employees, nameFilter, selectedFacilityId]);

  // 🚨 NUCLEAR BACKSTOP: JavaScript-based pseudo-element killer
  useEffect(() => {
    const killPseudoElements = () => {
      // Create a style element to inject CSS that will definitely work
      const styleElement = document.createElement('style');
      styleElement.id = 'directory-pseudo-killer';
      styleElement.textContent = `
        /* 💀 JAVASCRIPT-INJECTED NUCLEAR CSS OVERRIDE 💀 */
        .directory-page .main-navbar-tabs li a.angular-route::after,
        .directory-page .main-navbar-tabs li a.angular-route:after,
        .directory-page .main-navbar-tabs li a.react-route::after,
        .directory-page .main-navbar-tabs li a.react-route:after,
        .directory-page .main-navbar-tabs li a::after,
        .directory-page .main-navbar-tabs li a:after {
          display: none !important;
          content: "" !important;
          content: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          font-size: 0 !important;
          width: 0 !important;
          height: 0 !important;
          position: absolute !important;
          left: -9999px !important;
          z-index: -1 !important;
        }
      `;

      // Remove existing style if present and inject new one
      const existing = document.getElementById('directory-pseudo-killer');
      if (existing) existing.remove();
      document.head.appendChild(styleElement);

      // Add data attribute to body for CSS targeting
      document.body.setAttribute('data-directory-pseudo-killer', 'active');
    };

    // Run immediately and after a delay to catch late-loaded styles
    killPseudoElements();
    const timer = setTimeout(killPseudoElements, 100);

    return () => {
      clearTimeout(timer);
      const styleElement = document.getElementById('directory-pseudo-killer');
      if (styleElement) styleElement.remove();
      document.body.removeAttribute('data-directory-pseudo-killer');
    };
  }, []);

  // Facilities are loaded globally in ShellLayout - no need to load here

  /**
   * Load employees from API with fallback to mock data
   */
  const loadEmployees = async () => {
    try {
      const data = await fetchEmployees();

      if (data && data.length > 0) {
        const transformedData = transformEmployeeData(data);
        setEmployees(transformedData);
      } else {
        setEmployees(MOCK_EMPLOYEES);
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
      // Fallback to mock data
      setEmployees(MOCK_EMPLOYEES);
    }
  };

  /**
   * Apply search and filter criteria to employee list
   */
  const applyFilters = () => {
    let filtered = [...employees];

    // Apply name filter
    if (nameFilter) {
      const searchLower = nameFilter.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(searchLower) ||
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchLower)
      );
    }

    // Apply facility filter
    if (selectedFacilityId !== null) {
      const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
      if (selectedFacility) {
        // Filter employees who have this facility in their locations array
        filtered = filtered.filter(emp => {
          if (!emp.locations || emp.locations.length === 0) return false;
          return emp.locations.some(location =>
            location.toLowerCase() === selectedFacility.name.toLowerCase()
          );
        });
      }
    }

    setFilteredEmployees(filtered);
  };

  /**
   * Get facility options for filter dropdown (using facility IDs from API)
   */
  const getFacilityOptions = (): FilterOption[] => {
    return facilities.map(facility => ({
      value: facility.id.toString(),
      label: facility.name
    }));
  };


  /**
   * Handle employee row click - navigate to detail page
   */
  const handleEmployeeClick = (employee: Employee) => {
    navigate(`/directory/employee/${employee.id}`);
  };

  /**
   * Define table columns matching AngularJS design
   */
  const getTableColumns = (): TableColumn[] => [
    {
      key: 'name',
      title: 'Name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      onClick: (record) => handleEmployeeClick(record)
    },
    {
      key: 'email',
      title: 'Email'
    },
    {
      key: 'phone',
      title: 'Phone'
    },
    {
      key: 'locations',
      title: 'Locations',
      render: (value) => Array.isArray(value) ? value.join(', ') : value || '',
      onClick: (record) => handleEmployeeClick(record)
    }
  ];

  // Show loading state only on initial load
  if (isLoading && employees.length === 0) {
    return (
      <div className="tec-page">
        <div className="tec-container">
          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div>Loading employee directory...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tec-page directory-page">
      {/* Error Alert */}
      {error && (
        <div style={{
          padding: '10px',
          margin: '16px',
          background: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          Error Loading Directory: {error}
          <button
            onClick={clearError}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Directory Navigation with Tab Styling */}
      <div className="tec-tabs">
        <ul className="tec-tabs-nav">
          <li className="tec-tabs-nav-item">
            <Link
              to="/directory"
              className={`tec-tabs-nav-link ${location.pathname === '/directory' ? 'active' : ''}`}
            >
              EMPLOYEES
            </Link>
          </li>
          <li className="tec-tabs-nav-item">
            <Link
              to="/directory/referrals"
              className={`tec-tabs-nav-link ${location.pathname === '/directory/referrals' ? 'active' : ''}`}
            >
              REFERRALS
            </Link>
          </li>
        </ul>
      </div>

      {/* Filter Controls */}
      <div className="tec-container">

        <FilterControls
          facilityOptions={getFacilityOptions()}
          selectedFacility={selectedFacilityId ? selectedFacilityId.toString() : ''}
          onFacilityChange={(facility) => dispatch(setSelectedFacility(facility ? parseInt(facility) : null))}
          nameFilter={nameFilter}
          onNameChange={setNameFilter}
        />
      </div>

      {/* Employee Table */}
      <Table
        columns={getTableColumns()}
        data={filteredEmployees}
        loading={isLoading}
        emptyText="No employees found"
        onRowClick={handleEmployeeClick}
      />
    </div>
  );
};

export default DirectoryPage;